import type { MapMarkerIconOptions } from '@gpx-spa/geo-domain'

const DEFAULT_SIZE: [number, number] = [28, 36]

function escapeAttr(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

export function resolveMapMarkerIconLayout(icon: MapMarkerIconOptions): {
  size: [number, number]
  anchor: [number, number]
  popupAnchor: [number, number]
} {
  const size = icon.size ?? DEFAULT_SIZE
  const anchor = icon.anchor ?? [Math.round(size[0] / 2), size[1]]
  const popupAnchor = icon.popupAnchor ?? [0, -size[1] + 5]
  return { size, anchor, popupAnchor }
}

/**
 * Inline a `data:image/svg+xml` payload only when it is a single script-free `<svg>`.
 * Leaflet `L.icon` (`<img>`) often fails to paint SVG data URIs; `L.divIcon` + inner SVG does not.
 */
export function safeSvgMarkupFromDataUrl(url: string): string | null {
  if (!url.startsWith('data:image/svg+xml')) return null
  const comma = url.indexOf(',')
  if (comma < 0) return null
  const meta = url.slice('data:'.length, comma)
  const payload = url.slice(comma + 1)
  let svg: string
  try {
    svg = /;base64/i.test(meta) ? atob(payload) : decodeURIComponent(payload)
  } catch {
    return null
  }
  const trimmed = svg.trim()
  if (!/^<svg[\s>]/i.test(trimmed) || !/<\/svg>\s*$/i.test(trimmed)) return null
  const lower = trimmed.toLowerCase()
  if (
    lower.includes('<script') ||
    lower.includes('</script') ||
    /\son[a-z]+\s*=/i.test(trimmed)
  ) {
    return null
  }
  return trimmed
}

export function mapMarkerIconDivHtml(icon: MapMarkerIconOptions): string {
  const { size } = resolveMapMarkerIconLayout(icon)
  const svg = safeSvgMarkupFromDataUrl(icon.url)
  if (svg) {
    return `<div class="gpx-leaflet-svg-icon" style="width:${size[0]}px;height:${size[1]}px;line-height:0;overflow:visible">${svg}</div>`
  }
  return `<img src="${escapeAttr(icon.url)}" width="${size[0]}" height="${size[1]}" alt="" draggable="false" style="display:block;width:${size[0]}px;height:${size[1]}px;max-width:none;max-height:none;border:0" />`
}
